import { useId } from "react";
import {
  normalizeIconColor,
  resolveCmsIcon,
  resolveIconColor,
} from "@/lib/cms-puck/blocks/icons";
import { ColorField } from "@/components/cms/theme/ColorField";
import {
  blockStyleNeedsCssRules,
  buildBlockCssRules,
  normalizeBlockStyle,
  type BlockStyle,
  type ColorRef,
} from "@/lib/cms-puck/block-style";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import { useBlockStyleCss } from "@/hooks/use-block-style-css";
import { cmsNew } from "@/lib/cms/cms-new-i18n";

/**
 * IconBlock — render du bloc Icône en mode style `"self"` : le style va sur un
 * `<span inline-flex>` ajusté au contenu (badge autour de l'icône), pas sur un
 * conteneur pleine largeur. La couleur propre de l'icône reste pilotée par son
 * champ `color` (palette du thème via `ColorField`).
 */
export function IconColorField({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (value: ColorRef) => void;
}) {
  const theme = usePuckTheme();
  const colors = theme?.colors ?? [];
  return (
    <ColorField
      label={cmsNew("cms.new.auto.label.couleur", "Couleur")}
      colors={colors}
      value={normalizeIconColor(value)}
      onChange={onChange}
    />
  );
}

export function IconBlock({
  name,
  size,
  color,
  align,
  style,
}: {
  name: string;
  size: number;
  color: ColorRef | string;
  align: "left" | "center" | "right";
  style?: BlockStyle;
}) {
  const theme = usePuckTheme();
  const palette = theme?.colors ?? [];
  const css = useBlockStyleCss(style);
  const IconCmp = resolveCmsIcon(name);
  // Règles générées du style partagé (cf. ButtonBlock) : l'icône porte la classe.
  const uid = useId();
  const normalized = normalizeBlockStyle(style);
  const needsRules = blockStyleNeedsCssRules(normalized);
  const blockClass = `cms-blk-${uid.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const rules = needsRules
    ? buildBlockCssRules(`.${blockClass}`, normalized)
    : null;
  const attrs = normalized.attributes;
  const justify =
    align === "center"
      ? "justify-center"
      : align === "right"
        ? "justify-end"
        : "justify-start";
  return (
    <div className={`flex ${justify}`}>
      {rules ? <style>{rules}</style> : null}
      <span
        id={attrs.cssId ?? undefined}
        className={`inline-flex ${needsRules ? blockClass : ""} ${attrs.cssClass ?? ""}`}
        style={css}
      >
        {/* Couleur en propriété CSS : `var()` / `color-mix()` (couleurs du
            thème) ne sont pas fiables dans l'attribut SVG `stroke`. */}
        <IconCmp
          size={size}
          color="currentColor"
          style={{ color: resolveIconColor(palette, color) }}
        />
      </span>
    </div>
  );
}
